import { z } from 'zod';

export enum VisitType {
  PICKUP = 'PICKUP',
  DELIVERY = 'DELIVERY',
}

export const VisitTypeLabels: Record<VisitType, string> = {
  [VisitType.PICKUP]: 'Retiro',
  [VisitType.DELIVERY]: 'Entrega',
};

export interface ServiceVisitTypeOperator {
  id: string;
  name?: string;
  businessName?: string;
}

export interface ServiceVisitType {
  id: string;
  name: string;
  logisticOperatorId: string | null;
  logisticOperator?: ServiceVisitTypeOperator | null;
  jsonConfig: Record<string, unknown> | null;
  type: VisitType;
  createdAt?: string;
  updatedAt?: string;
}

export interface NewServiceVisitType {
  name: string;
  type: VisitType;
  logisticOperatorId?: string | null;
  jsonConfig?: Record<string, unknown> | null;
}

export interface UpdateServiceVisitType {
  name?: string;
  type?: VisitType;
  logisticOperatorId?: string | null;
  jsonConfig?: Record<string, unknown> | null;
}

export interface ServiceVisitTypeFilters {
  name?: string;
  logisticOperatorId?: string;
  type?: VisitType;
}

const jsonConfigStringSchema = z
  .string()
  .optional()
  .refine(
    (value) => {
      if (!value || value.trim() === '') return true;
      try {
        const parsed = JSON.parse(value);
        return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
      } catch {
        return false;
      }
    },
    { message: 'La configuración debe ser un objeto JSON válido' }
  );

export const serviceVisitTypeFormSchema = z.object({
  name: z
    .string()
    .min(1, 'El nombre es requerido')
    .max(200, 'El nombre no puede exceder 200 caracteres'),
  type: z.nativeEnum(VisitType, {
    errorMap: () => ({ message: 'El tipo de visita es requerido' }),
  }),
  logisticOperatorId: z.string().optional().or(z.literal('')),
  jsonConfig: jsonConfigStringSchema,
});

export type ServiceVisitTypeFormValues = z.infer<
  typeof serviceVisitTypeFormSchema
>;

export const defaultServiceVisitTypeValues: ServiceVisitTypeFormValues = {
  name: '',
  type: VisitType.DELIVERY,
  logisticOperatorId: '',
  jsonConfig: '',
};

/** Convierte los valores del formulario al payload que espera el backend. */
export const toServiceVisitTypePayload = (
  values: ServiceVisitTypeFormValues
): NewServiceVisitType => ({
  name: values.name.trim(),
  type: values.type,
  logisticOperatorId:
    values.logisticOperatorId && values.logisticOperatorId.trim() !== ''
      ? values.logisticOperatorId.trim()
      : null,
  jsonConfig:
    values.jsonConfig && values.jsonConfig.trim() !== ''
      ? (JSON.parse(values.jsonConfig) as Record<string, unknown>)
      : null,
});

/** Convierte un tipo de visita existente a los valores del formulario. */
export const toServiceVisitTypeFormValues = (
  serviceVisitType: ServiceVisitType
): ServiceVisitTypeFormValues => ({
  name: serviceVisitType.name ?? '',
  type: serviceVisitType.type ?? VisitType.DELIVERY,
  logisticOperatorId: serviceVisitType.logisticOperatorId ?? '',
  jsonConfig: serviceVisitType.jsonConfig
    ? JSON.stringify(serviceVisitType.jsonConfig, null, 2)
    : '',
});
